import { useTranslation } from "react-i18next";
import { useToastMessage } from "../hooks/useToastMessage";
import { APP_ID } from "../constants";
import {
  AccessToken,
  LoginErrorCode,
  isObject,
  isValidAppApiError,
  LoginMethod,
  AuthData,
} from "../types";
import {
  contractEnginesAtom,
  EngineTypesMap,
  outputStenScoreAtom,
} from "../store";
import { useCallback } from "react";
import { useAtom } from "jotai";
import { voislogApi } from "./apiClients/voislogApi";
import { ApiError } from "./apiClients/ApiError";
import { decodeJwt } from "./decodeJwt";
import dayjs from "dayjs";
import { getSelectableEngineList } from "../pages/engines";

export function expToExpiredAt(access_token: string): string {
  const decoded = decodeJwt(access_token);
  return dayjs.unix(decoded.exp ?? 0).toISOString();
}

function getLoginErrorCode(method: LoginMethod): LoginErrorCode {
  switch (method) {
    case LoginMethod.LoginForm:
      return "INVALID_TOKEN";

    case LoginMethod.GoogleAuth:
      return "INVALID_GOOGLE_TOKEN";
  }
}

// クライアントアプリのバージョンが古い(426)場合、案内後にページを再読み込みする
function handleUpgradeRequired(e: unknown, message: string): boolean {
  if (!(e instanceof ApiError) || e.status !== 426) return false;
  window.alert(message);
  window.location.reload();
  return true;
}

type GetAccessToken = <T extends LoginMethod>(
  method: T,
  onInvalidUserInfo: (errorCode: LoginErrorCode) => void,
  data: AuthData[T],
) => Promise<AccessToken | false>;

export function useGetAccessToken(): GetAccessToken {
  const toastMessage = useToastMessage();
  const { t, i18n } = useTranslation();
  const [, setOutputStenScore] = useAtom(outputStenScoreAtom);
  const [, setContractEngines] = useAtom(contractEnginesAtom);

  const requestAuthToken = async (
    authMethod: LoginMethod,
    authData: unknown,
  ): Promise<Omit<AccessToken, "user_id" | "company_id">> => {
    switch (authMethod) {
      case LoginMethod.LoginForm: {
        const data = authData as AuthData[LoginMethod.LoginForm];
        const {
          data: resData,
          error,
          response,
        } = await voislogApi.POST("/v2/core/auth/token", {
          body: { email: data.email, password: data.password },
        });
        if (!resData) throw new ApiError(response, "post", error);
        return {
          access_token: resData.access_token,
          expired_at: expToExpiredAt(resData.access_token),
        };
      }

      case LoginMethod.GoogleAuth: {
        const data = authData as AuthData[LoginMethod.GoogleAuth];
        const {
          data: resData,
          error,
          response,
        } = await voislogApi.POST("/v2/core/auth/google/token", {
          body: { credential: data.credential },
        });
        if (error) throw new ApiError(response, "post", error);
        return {
          access_token: resData.access_token,
          expired_at: expToExpiredAt(resData.access_token),
        };
      }
    }
  };

  const handleLoginError = useCallback(
    (
      e: unknown,
      method: LoginMethod,
      onInvalidUserInfo: (errorCode: LoginErrorCode) => void,
      authData: unknown,
    ): void => {
      const body = e instanceof ApiError ? e.body : e;

      if (
        !isValidAppApiError(body) ||
        !isObject((body as { detail?: unknown }).detail)
      ) {
        toastMessage(e);
        return;
      }

      const errDetail = (body as { detail: { type: string } }).detail;
      if (errDetail.type === "auth_error.invalid_input") {
        onInvalidUserInfo(getLoginErrorCode(method));
      } else if (
        method == LoginMethod.LoginForm &&
        errDetail.type === "auth_error.locked_temporary"
      ) {
        const data = authData as AuthData[LoginMethod.LoginForm];
        toastMessage(e);
        voislogApi
          .POST("/v2/core/auth/unlock/request", {
            body: {
              email: data.email,
              app_url: `${location.origin}/${i18n.resolvedLanguage}/account-unlocked`,
              language: i18n.resolvedLanguage as "ja" | "en",
            },
          })
          .then(({ error, response }) => {
            if (error) throw new ApiError(response, "post", error);
          })
          .catch((e) => {
            console.error("Unlock to Temporary Locked Error!", e);
            toastMessage(e);
          });
      } else {
        toastMessage(e);
      }
    },
    [i18n.resolvedLanguage, toastMessage],
  );

  return useCallback(
    async (method, onInvalidUserInfo, authData) => {
      try {
        const tokenData = await requestAuthToken(method, authData);

        // ユーザー情報の取得
        const {
          data: me,
          error: meError,
          response: meResponse,
        } = await voislogApi.GET("/v2/core/users/{user_id_or_me}", {
          params: { path: { user_id_or_me: "me" } },
          headers: { Authorization: `Bearer ${tokenData.access_token}` },
        });
        if (meError) throw new ApiError(meResponse, "get", meError);
        const user_id = me?.id;
        const company_id = me?.companies?.find((c) => c.is_member)?.id;
        if (!user_id || !company_id) {
          onInvalidUserInfo("ENGINE_DISABLED");
          return false;
        }

        // 契約エンジンの確認
        const {
          data: contracts,
          error: contractsError,
          response: contractsResponse,
        } = await voislogApi.GET("/v2/core/company-contracts", {
          params: {
            query: {
              app: APP_ID,
              company_id,
              valid_today: true,
            },
          },
          headers: { Authorization: `Bearer ${tokenData.access_token}` },
        });
        if (contractsError)
          throw new ApiError(contractsResponse, "get", contractsError);
        if (!contracts || contracts.length === 0) {
          onInvalidUserInfo("ENGINE_DISABLED");
          return false;
        }

        const outputStenScore = contracts.some(
          (c) => c.contract_plan?.engine_options?.output_sten_score === true,
        );
        setOutputStenScore(outputStenScore);

        const availableEngines = contracts.map((c) => c.contract_plan.engine);
        const engineMap: EngineTypesMap = Object.fromEntries(
          availableEngines.map((e) => [e, true]),
        );
        const appEngines = getSelectableEngineList(engineMap);
        if (appEngines.length === 0) {
          // 有効な解析種別がない場合はエンジン無効エラー
          onInvalidUserInfo("ENGINE_DISABLED");
          return false;
        }
        setContractEngines(availableEngines);

        return { ...tokenData, user_id, company_id };
      } catch (e) {
        console.error("Get Access Token Error!", e);

        if (handleUpgradeRequired(e, t("Error.loginError.upgrade_required")))
          return false;

        handleLoginError(e, method, onInvalidUserInfo, authData);
        return false;
      }
    },
    [t, handleLoginError, setOutputStenScore, setContractEngines],
  );
}
